/**
 * A data-table cell that reveals its column's provenance on click.
 *
 * The deterministic engine doesn't track per-cell lineage, so provenance is COLUMN-level: each output
 * column maps to "OutTable.OutField" (or "(static value)" / "(computed)") via the resolved config's
 * field defs. Clicking a cell shows "This value came from [source field] in your data". Keyboard
 * accessible (the cell is a button; Enter/Space toggles; Escape closes).
 */
import { useState } from "react";

export function ProvenanceCell({
  value,
  source,
  invalid,
  warn,
}: {
  value: string;
  /** "OutTable.OutField" | "(static value)" | "(computed)" | undefined. */
  source?: string;
  /** Highlight red (a validation error touches this cell). */
  invalid?: boolean;
  /** Highlight yellow (a validation warning touches this cell). */
  warn?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const cls = [
    "prov-cell",
    invalid ? "prov-cell--error" : warn ? "prov-cell--warn" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const sourceText = source
    ? source.startsWith("(")
      ? `This value is a ${source.replace(/[()]/g, "")}.`
      : `This value came from ${source} in your data.`
    : "Source field not available for this column.";

  return (
    <span
      className={cls}
      style={{
        display: "inline-block",
        position: "relative",
        background: invalid
          ? "var(--danger-bg, #fde8e8)"
          : warn
            ? "var(--warning-bg, #fdf6e3)"
            : undefined,
        padding: invalid || warn ? "0 4px" : undefined,
        borderRadius: 4,
      }}
    >
      <button
        type="button"
        className="prov-cell__trigger"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        aria-expanded={open}
        title={sourceText}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
          color: "inherit",
          cursor: "help",
          textAlign: "left",
        }}
      >
        {value}
      </button>
      {open && (
        <span
          role="tooltip"
          className="prov-cell__pop card card--pad-sm"
          style={{
            position: "absolute",
            zIndex: 20,
            top: "100%",
            left: 0,
            minWidth: 220,
            marginTop: 4,
            boxShadow: "var(--shadow-2, 0 6px 24px rgba(0,0,0,.15))",
            fontSize: "0.85rem",
            whiteSpace: "normal",
          }}
        >
          {sourceText}
        </span>
      )}
    </span>
  );
}
